import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography
} from 'antd';
import {
    EditOutlined,
    ReloadOutlined,
    SearchOutlined,
    UserAddOutlined
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { AppState } from '../store';
import { getTbcRolSystems, getTbcUsers, postTbcUser, putTbcUser } from '../store/user/thunks';
import { PlanRolSystem, TbcPortalUser, TbcPortalUserRequest } from '../store/user/types';

const { Title, Text } = Typography;

const TBC_ORGANIZATION_NAME = 'CODING THEBLUE';

const TbcUserManagement: React.FC<any> = (props: any) => {
    const [form] = Form.useForm();
    const [users, setUsers] = useState<TbcPortalUser[]>([]);
    const [rolSystems, setRolSystems] = useState<PlanRolSystem[]>([]);
    const [search, setSearch] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<TbcPortalUser | null>(null);

    const filteredUsers = useMemo(() => {
        const value = search.trim().toLowerCase();
        if (!value) {
            return users;
        }

        return users.filter(user =>
            user.dni.toLowerCase().includes(value)
            || user.fullName.toLowerCase().includes(value)
            || String(user.email || '').toLowerCase().includes(value)
            || String(user.rolName || '').toLowerCase().includes(value)
            || String(user.systemLabel || '').toLowerCase().includes(value)
            || String(user.status || '').toLowerCase().includes(value)
        );
    }, [users, search]);

    const loadData = async () => {
        const [userResponse, rolSystemResponse] = await Promise.all([
            props.getTbcUsers(),
            props.getTbcRolSystems()
        ]);
        setUsers(userResponse || []);
        setRolSystems(rolSystemResponse || []);
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreateModal = () => {
        setSelectedUser(null);
        form.resetFields();
        form.setFieldsValue({
            status: 'ACTIVO'
        });
        setIsModalOpen(true);
    };

    const openEditModal = (user: TbcPortalUser) => {
        setSelectedUser(user);
        form.resetFields();
        form.setFieldsValue({
            dni: user.dni,
            fullName: user.fullName,
            email: user.email,
            phone: user.phone,
            observation: user.observation,
            status: user.status || 'ACTIVO',
            rolSystemId: user.rolSystemId
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
        form.resetFields();
    };

    const handleSubmit = async () => {
        const values = await form.validateFields();
        const request: TbcPortalUserRequest = {
            id: selectedUser?.id || null,
            dni: values.dni,
            fullName: values.fullName,
            email: values.email || null,
            phone: values.phone || null,
            observation: values.observation || null,
            status: values.status,
            rolSystemId: values.rolSystemId
        };

        const response = selectedUser
            ? await props.putTbcUser(request)
            : await props.postTbcUser(request);

        if (response) {
            await loadData();
            closeModal();
        }
    };

    const columns = [
        {
            title: 'Usuario',
            dataIndex: 'fullName',
            key: 'fullName',
            render: (value: string, row: TbcPortalUser) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{value}</Text>
                    <Text type="secondary">{row.dni}</Text>
                </Space>
            )
        },
        {
            title: 'Contacto',
            key: 'contact',
            render: (_: unknown, row: TbcPortalUser) => (
                <Space direction="vertical" size={0}>
                    <Text>{row.email || 'Sin email'}</Text>
                    <Text type="secondary">{row.phone || 'Sin telefono'}</Text>
                </Space>
            )
        },
        {
            title: 'Rol portal',
            key: 'role',
            render: (_: unknown, row: TbcPortalUser) => (
                <Space direction="vertical" size={0}>
                    <Text>{row.rolName || 'Sin rol'}</Text>
                    <Text type="secondary">{row.systemLabel || 'TBC'}</Text>
                </Space>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            render: (value: string) => {
                const color = value === 'ACTIVO' ? 'green' : value === 'BLOQUEADO' ? 'orange' : 'red';
                return <Tag color={color}>{value}</Tag>;
            }
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            align: 'right' as const,
            render: (_: unknown, row: TbcPortalUser) => (
                <Button icon={<EditOutlined />} onClick={() => openEditModal(row)}>
                    Editar
                </Button>
            )
        }
    ];

    return (
        <div className="tbc-admin-page">
            <Space direction="vertical" size={18} style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ marginBottom: 4 }}>Usuarios del portal</Title>
                    <Text type="secondary">Creacion y administracion de usuarios internos para el portal The Blue Coding.</Text>
                </div>

                <Alert
                    type="info"
                    showIcon
                    message={`Organizacion asignada por defecto: ${TBC_ORGANIZATION_NAME}`}
                    description="Los roles disponibles pertenecen solo a sistemas del tenant TBC."
                />

                <Card>
                    <Row gutter={[12, 12]} align="bottom">
                        <Col xs={24} md={12}>
                            <Text strong>Buscar usuario</Text>
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Identificacion, nombre, correo, rol o estado"
                                style={{ marginTop: 6 }}
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <Button icon={<ReloadOutlined />} block onClick={loadData}>
                                Actualizar
                            </Button>
                        </Col>
                        <Col xs={24} md={4}>
                            <Button icon={<UserAddOutlined />} type="primary" block onClick={openCreateModal}>
                                Nuevo usuario
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={filteredUsers}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 900 }}
                />

                <Modal
                    open={isModalOpen}
                    title={selectedUser ? 'Editar usuario' : 'Nuevo usuario'}
                    className="tbc-admin-modal"
                    okText={selectedUser ? 'Actualizar' : 'Crear'}
                    cancelText="Cancelar"
                    onOk={handleSubmit}
                    onCancel={closeModal}
                    destroyOnHidden
                    forceRender
                    width={760}
                >
                    <Form form={form} layout="vertical">
                        <Alert
                            type="success"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={TBC_ORGANIZATION_NAME}
                            description="El usuario se guardara dentro de esta organizacion."
                        />

                        <Row gutter={[16, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Identificacion"
                                    name="dni"
                                    rules={[{ required: true, message: 'Ingrese la identificacion' }]}
                                >
                                    <Input placeholder="1727828447" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Nombre completo"
                                    name="fullName"
                                    rules={[{ required: true, message: 'Ingrese el nombre completo' }]}
                                >
                                    <Input placeholder="Pablo Santacruz" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Correo"
                                    name="email"
                                    rules={[{ type: 'email', message: 'Ingrese un correo valido' }]}
                                >
                                    <Input placeholder="usuario@thebluecoding.com" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Telefono" name="phone">
                                    <Input placeholder="0999999999" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Rol y sistema"
                                    name="rolSystemId"
                                    rules={[{ required: true, message: 'Seleccione el rol del portal' }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Seleccione el acceso"
                                        optionFilterProp="label"
                                        options={rolSystems.map(item => ({
                                            value: item.rolSystemId,
                                            label: `${item.systemLabel || item.systemName || 'Portal'} - ${item.rolName}`
                                        }))}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Estado"
                                    name="status"
                                    rules={[{ required: true, message: 'Seleccione el estado' }]}
                                >
                                    <Select
                                        options={[
                                            { value: 'ACTIVO', label: 'ACTIVO' },
                                            { value: 'INACTIVO', label: 'INACTIVO' },
                                            { value: 'BLOQUEADO', label: 'BLOQUEADO' }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Form.Item label="Observacion" name="observation">
                                    <Input.TextArea rows={3} placeholder="Comentario administrativo opcional" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Modal>
            </Space>
        </div>
    );
};

const mapStateToProps = (state: AppState) => ({});

export default connect(mapStateToProps, {
    getTbcUsers,
    getTbcRolSystems,
    postTbcUser,
    putTbcUser
})(TbcUserManagement);
